import { useLayoutEffect, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import { useAtom } from "jotai"
import { useAsync } from "@mylaunch/components/hooks"
import { Result } from "@praha/byethrow"
import { toast } from "sonner"
import { Button } from "~/components/ui/button"
import { AsyncButton } from "~/components/ui/async-button"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "~/components/ui/input-otp"
import { cn } from "~/lib/utils"
import { loginDialogAtom } from "../atoms/login"
import { sendOTP, verifyOTP } from "../server/auth.functions"
import { useWebHaptics } from "web-haptics/react"
import { isStandalone } from "@mylaunch/pwa/lib/platform"
import type { User } from "@apps/gwservice/types"

export function LoginStep2() {
  const [state, setState] = useAtom(loginDialogAtom)
  const [code, setCode] = useState("")
  const [error, setError] = useState(false)
  const { loading, run } = useAsync((phone: string, code: string) =>
    verifyOTP({
      data: {
        phone,
        code,
      },
    }))
  const router = useRouter()
  const queryClient = useQueryClient()
  const otpRef = useRef<HTMLInputElement>(null)
  // Sync focus (no setTimeout) so iOS keeps the tap activation window open and shows the keyboard.
  // Radix Dialog FocusScope already ran on dialog open, not on this step change, so it won't reclaim focus.
  useLayoutEffect(() => {
    otpRef.current?.focus()
  }, [])
  const { trigger } = useWebHaptics()

  const handleBack = () => setState({ ...state, step: 1 })

  const handleVerify = async (value: string) => {
    const result = await run(state.phone, value)
    if (Result.isSuccess(result) && isUser(result.value)) {
      if (!result.value.name) {
        window.location.assign("/profile/edit")
        return
      }
      if (isStandalone()) {
        await queryClient.invalidateQueries()
        await queryClient.refetchQueries({ type: "active" })
        await router.invalidate()
        return
      }
      window.location.assign(window.location.pathname + window.location.search)
      return
    }

    trigger("error")
    setCode("")
    if (Result.isSuccess(result) && result.value === "ATTEMPTS_EXCEEDED") {
      setState({
        ...state,
        step: 1,
      })
      toast.error("Слишком много попыток. Запросите новый код")
      return
    } else {
      setError(true)
      toast.error("Неверный код")
      setTimeout(() => otpRef.current?.focus(), 50)
    }
  }

  const handleChange = (value: string) => {
    if (error) setError(false)
    setCode(value)
    if (value.length === 4) handleVerify(value)
  }

  const slotClass = cn(
    "size-16 text-xl rounded-xl border border-input first:rounded-xl last:rounded-xl",
    error && "border-destructive",
  )

  return (
    <div className="flex flex-col gap-m">
      <h2 className="text-center text-lg font-semibold">Введите 4-значный код</h2>
      <div>
        Введите код из СМС по номеру
        <div className="flex items-center gap-s">
          {state.phone}
          <Button variant="link" onClick={handleBack} className="text-secondary underline p-0 h-auto">
            Изменить номер
          </Button>
        </div>
      </div>
      <InputOTP
        ref={otpRef}
        maxLength={4}
        value={code}
        onChange={handleChange}
        disabled={loading}
        containerClassName="justify-center"
      >
        <InputOTPGroup className="gap-3">
          <InputOTPSlot index={0} className={slotClass} />
          <InputOTPSlot index={1} className={slotClass} />
          <InputOTPSlot index={2} className={slotClass} />
          <InputOTPSlot index={3} className={slotClass} />
        </InputOTPGroup>
      </InputOTP>
      <p className="text-sm text-muted-foreground">
        Не пришел код?{" "}
        <AsyncButton
          fn={() =>
            sendOTP({
              data: {
                phone: state.phone,
              },
            })}
          variant="link"
          className="text-muted-foreground underline p-0 h-auto"
        >
          Отправить еще раз
        </AsyncButton>
      </p>
    </div>
  )
}

const isUser = (value: unknown): value is User => {
  if (typeof value !== "object" || value === null) {
    return false
  }

  return "id" in value
}
